import { useState, useEffect } from "react";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { testimonials } from "../../data/testimonials";
import Reveal from "../shared/Reveal";
import BorderBeamCard from "../shared/kokonut/BorderBeamCard";

export const Testimonials = () => {
  const { lang } = useLang();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % testimonials.length), 5500);
    return () => clearInterval(id);
  }, [paused]);

  // pair view on desktop, one per mobile — show current + next on desktop
  const visible = [testimonials[idx], testimonials[(idx + 1) % testimonials.length]];

  return (
    <section
      className="relative py-20 md:py-32"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <Reveal variant="fade-up" className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs mono-label text-cyan-400 mb-3">
              {lang === "en" ? "TESTIMONIALS" : "شهادات العملاء"}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-strong">
              {lang === "en"
                ? "Leaders trust Gulf TechLab to deliver."
                : "القادة يثقون بخليج تك لاب لتحقيق النتائج."}
            </h2>
          </div>

          {/* Nav */}
          <div className="flex items-center gap-3">
            <button
              aria-label="Previous"
              onClick={() => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length)}
              data-testid="testimonial-prev"
              className="w-10 h-10 rounded-full glass hover:border-cyan-400/50 flex items-center justify-center text-mid hover:text-cyan-300 transition"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              aria-label="Next"
              onClick={() => setIdx((i) => (i + 1) % testimonials.length)}
              data-testid="testimonial-next"
              className="w-10 h-10 rounded-full glass hover:border-cyan-400/50 flex items-center justify-center text-mid hover:text-cyan-300 transition"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-5 min-h-[280px]">
          {visible.map((ti, i) => (
            <BorderBeamCard
              key={`${idx}-${i}`}
              data-testid={`testimonial-${(idx + i) % testimonials.length}`}
              className="p-8 hover-lift transition-all animate-fade-up"
              style={{ animationDelay: `${i * 120}ms` }}
              beamDuration={i === 0 ? 5 : 6.5}
            >
              <Quote className="w-8 h-8 text-cyan-400/40 mb-4" />
              <p className="text-lg text-strong leading-relaxed mb-6">
                &ldquo;{lang === "en" ? ti.quote_en : ti.quote_ar}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-hair">
                <div className="w-10 h-10 rounded-full gradient-cyan flex items-center justify-center text-slate-950 font-bold">
                  {ti.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium text-strong">{ti.name}</div>
                  <div className="text-xs text-soft">{lang === "en" ? ti.role_en : ti.role_ar}</div>
                </div>
              </div>
            </BorderBeamCard>
          ))}
        </div>

        {/* Progress dots */}
        <div className="mt-8 flex items-center justify-center gap-1.5">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`testimonial-dot-${i}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === idx ? "w-8 bg-cyan-400 shadow-[0_0_12px_rgba(0,229,255,0.5)]" : "w-3 bg-soft hover:bg-mid"
              }`}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
